import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils/queryParams';
import { ENRICHMENT_TYPE } from './constants';

export interface EnrichmentService {
  code: string;
  label: string;
  description?: string;
}

export interface EnrichmentServicesResponse {
  results: EnrichmentService[];
}

export interface EnrichmentRequirements {
  maxComponents: number;
  required?: string[];
  responseStructure: {
    insights: string;
  };
}

export interface ExecuteEnrichmentParams {
  id: string;
  providerId: string | number;
  components: unknown[];
  requirements: EnrichmentRequirements;
}

export interface EnrichmentExecutionResult {
  response: {
    insights: {
      label: string;
      summary?: string;
      components: Record<string, unknown>[];
    }[];
  };
}

export interface ClassificationSignal {
  signal_type: string;
  actual_value: number;
  threshold: number;
  operator: string;
  matched: boolean;
  code_set_id?: number;
  payer_type?: string;
  metric: string;
  // Date range (displayed when present)
  min_date?: string;
  max_date?: string;
}

export interface FreshFacesSignal {
  signal_type: 'fresh_faces';
  matched: boolean;
  strategy_id?: number;
  lookback_months?: number;
  strategy_label?: string;
  strategy_description?: string;
  // May also carry standard fields with missing/zero values
  actual_value?: number;
  threshold?: number;
  operator?: string;
  metric?: string;
  code_set_id?: number;
  payer_type?: string;
}

export type AnyClassificationSignal = ClassificationSignal | FreshFacesSignal;

export const isFreshFacesSignal = (
  signal: AnyClassificationSignal
): signal is FreshFacesSignal =>
  signal.signal_type === 'fresh_faces' ||
  ('type' in signal && (signal as any).type === 'fresh_faces');

export const isStandardSignal = (
  signal: AnyClassificationSignal
): signal is ClassificationSignal => !isFreshFacesSignal(signal);

export interface Classification {
  name: string;
  description: string;
  signals: AnyClassificationSignal[];
}

export interface ProviderClassificationsResult {
  id: number;
  company: number;
  key: string;
  provider: string;
  content: Classification[];
  content_type: string;
  created_at: string;
}

export const enrichmentApi = createApi({
  reducerPath: 'enrichment',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [ENRICHMENT_TYPE],
  endpoints: (builder) => ({
    getEnrichmentServices: builder.query<
      EnrichmentServicesResponse,
      Record<string, unknown> | void
    >({
      query: (params) => {
        const queryParams = params
          ? buildSearchParamsAuto(params as Record<string, unknown>)
          : null;
        const queryString = queryParams?.toString();
        return `v1/enrichments/${queryString ? '?' + queryString : ''}`;
      },
      providesTags: (result) => {
        const items = Array.isArray(result) ? result : result?.results ?? [];
        return [
          ...items.map(({ code }) => ({
            type: ENRICHMENT_TYPE,
            id: code,
          })),
          { type: ENRICHMENT_TYPE, id: 'LIST' },
        ];
      },
    }),

    createEnrichmentService: builder.mutation<
      EnrichmentExecutionResult,
      ExecuteEnrichmentParams
    >({
      query: ({ id, providerId, components, requirements }) => ({
        url: `v1/enrichments/${id}/execute/`,
        method: 'POST',
        body: {
          provider_id: providerId,
          schema: {
            components,
            requirements,
          },
        },
      }),
      invalidatesTags: [{ type: ENRICHMENT_TYPE, id: 'LIST' }],
    }),
    getProviderClassifications: builder.query<
      ProviderClassificationsResult,
      { providerId: string | number }
    >({
      query: ({ providerId }) =>
        `v1/enrichments/content/provider-classifications/${providerId}/`,
    }),
  }),
});

export const {
  useGetEnrichmentServicesQuery,
  useCreateEnrichmentServiceMutation,
  useGetProviderClassificationsQuery,
} = enrichmentApi;
